// 案件レーンボードのレーン構築。依存ゼロの純関数（scripts/test-lane-core.mjs で単体テスト）。
// 仕様:
// - 未分類レーン（project_id NULL or 孤児 project_id）は domain フィルタに関わらず常に先頭
// - 要判断 = agent_jobs needs_decision の missionId に一致（要判断 > 今日 > 進行中 の優先）
// - 今日 = dueDate <= 今日（既存 "today" フィルタと同じ判定）

export type DomainFilter = "all" | "work" | "life";
export type LaneSection = "today" | "decision" | "active";

export interface LaneTask {
  id: string;
  title: string;
  completed: boolean;
  deleted: boolean;
  dueDate: string | null;
  priority: string;
  projectId?: string | null;
  repeatRule?: string | null;
}

export interface LaneProject {
  id: string;
  name: string;
  kind: "work" | "life";
  color: string;
  archived: boolean;
}

export interface LaneJob {
  status: string;
  missionId: string | null;
}

export interface Lane<T extends LaneTask = LaneTask, P extends LaneProject = LaneProject> {
  project: P | null;
  sections: { today: T[]; decision: T[]; active: T[] };
  total: number;
}

export function todayIso(now: Date = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

export function decisionTaskIds(jobs: LaneJob[]): Set<string> {
  const ids = new Set<string>();
  for (const job of jobs) {
    if (job.status === "needs_decision" && job.missionId) ids.add(job.missionId);
  }
  return ids;
}

export function sectionFor(task: LaneTask, decisionIds: Set<string>, today: string): LaneSection {
  if (decisionIds.has(task.id)) return "decision";
  if (task.dueDate && task.dueDate <= today) return "today";
  return "active";
}

function makeLane<T extends LaneTask, P extends LaneProject>(
  project: P | null,
  tasks: T[],
  decisionIds: Set<string>,
  today: string
): Lane<T, P> {
  const sections = { today: [] as T[], decision: [] as T[], active: [] as T[] };
  for (const task of tasks) sections[sectionFor(task, decisionIds, today)].push(task);
  return { project, sections, total: tasks.length };
}

export function buildLanes<T extends LaneTask, P extends LaneProject>(
  tasks: T[],
  projects: P[],
  jobs: LaneJob[],
  domain: DomainFilter,
  today: string
): Lane<T, P>[] {
  const active = tasks.filter((t) => !t.completed && !t.deleted);
  const decisionIds = decisionTaskIds(jobs);
  const known = new Set(projects.map((p) => p.id));
  const archivedIds = new Set(projects.filter((p) => p.archived).map((p) => p.id));
  // visible = non-archived projects that pass the domain filter
  const visible = projects.filter(
    (p) => !p.archived && (domain === "all" || p.kind === domain)
  );
  // unassigned: no projectId, orphan projectId, or projectId on an archived project.
  // domain フィルタで非表示になったタスクは未分類に流さない（合流は archived 限定）。
  const unassigned = active.filter((t) => !t.projectId || !known.has(t.projectId) || archivedIds.has(t.projectId));
  return [
    makeLane<T, P>(null, unassigned, decisionIds, today),
    ...visible.map((p) =>
      makeLane(p, active.filter((t) => t.projectId === p.id), decisionIds, today)
    ),
  ];
}
